
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, ArrowRight, CheckCircle, AlertCircle, Info } from 'lucide-react';

interface WorkflowCleaningReportProps {
  runId: string;
}

export const WorkflowCleaningReport = ({ runId }: WorkflowCleaningReportProps) => {
  const { data: rawRows } = useQuery({
    queryKey: ['workflow-data-raw-compare', runId],
    queryFn: async () => {
      const { data } = await supabase
        .from('workflow_data')
        .select('row_data, row_index')
        .eq('workflow_run_id', runId)
        .eq('data_type', 'raw')
        .order('row_index')
        .limit(50);
      return data || [];
    },
  });

  const { data: cleanedRows } = useQuery({
    queryKey: ['workflow-data-cleaned-compare', runId],
    queryFn: async () => {
      const { data } = await supabase
        .from('workflow_data')
        .select('row_data, row_index')
        .eq('workflow_run_id', runId)
        .eq('data_type', 'cleaned')
        .order('row_index')
        .limit(50);
      return data || [];
    },
  });

  if (!rawRows?.length || !cleanedRows?.length) {
    return (
      <div className="py-12 text-center">
        <Sparkles className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground">No cleaning data available for this run.</p>
      </div>
    );
  }

  // Compute differences between raw and cleaned
  const changes: Array<{
    rowIndex: number;
    field: string;
    before: string;
    after: string;
    reason: string;
  }> = [];

  rawRows.forEach((rawRow, i) => {
    const cleanedRow = cleanedRows[i];
    if (!cleanedRow) return;

    const rawData = rawRow.row_data as Record<string, unknown>;
    const cleanedData = cleanedRow.row_data as Record<string, unknown>;

    // Compare regular fields (not AI-generated ones)
    Object.keys(rawData).forEach(key => {
      if (key.startsWith('_')) return;
      const rawVal = String(rawData[key] ?? '');
      const cleanedVal = String(cleanedData[key] ?? '');
      if (rawVal !== cleanedVal) {
        changes.push({
          rowIndex: rawRow.row_index + 1,
          field: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          before: rawVal || '(empty)',
          after: cleanedVal || '(empty)',
          reason: inferReason(rawVal, cleanedVal),
        });
      }
    });

    // Check for AI-added fields
    Object.keys(cleanedData).forEach(key => {
      if (key.startsWith('_') && !(key in rawData)) {
        changes.push({
          rowIndex: rawRow.row_index + 1,
          field: key.replace(/^_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          before: '—',
          after: String(cleanedData[key] ?? ''),
          reason: 'AI-generated field added during cleaning',
        });
      }
    });
  });

  // Get AI notes from cleaned data
  const aiNotes = cleanedRows[0]?.row_data
    ? (cleanedRows[0].row_data as Record<string, unknown>)._ai_notes
    : null;

  const summaryStats = {
    totalRecords: rawRows.length,
    changedFields: changes.filter(c => c.before !== '—').length,
    addedFields: changes.filter(c => c.before === '—').length,
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <p className="text-2xl font-semibold">{summaryStats.totalRecords}</p>
          <p className="text-sm text-muted-foreground">Records Analyzed</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-semibold text-green-600">{summaryStats.changedFields}</p>
          <p className="text-sm text-muted-foreground">Fields Updated</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-semibold text-blue-600">{summaryStats.addedFields}</p>
          <p className="text-sm text-muted-foreground">AI Fields Added</p>
        </Card>
      </div>

      {/* AI Explanation */}
      {aiNotes && (
        <Card className="p-4 bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-green-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-1">AI Cleaning Summary</p>
              <p className="text-sm text-green-700 dark:text-green-400">
                {typeof aiNotes === 'string' ? aiNotes : 'The AI analyzed your CRM data and applied automatic corrections including normalization, deduplication, and data enrichment.'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Changes Table */}
      {changes.length > 0 ? (
        <div>
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-green-600" />
            Changes Made by AI
          </h4>
          <ScrollArea className="w-full max-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Row</TableHead>
                  <TableHead>Field</TableHead>
                  <TableHead>Before</TableHead>
                  <TableHead className="w-[30px]" />
                  <TableHead>After</TableHead>
                  <TableHead>Why This Changed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {changes.slice(0, 50).map((change, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs text-muted-foreground">#{change.rowIndex}</TableCell>
                    <TableCell className="font-medium text-sm">{change.field}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                      {change.before}
                    </TableCell>
                    <TableCell>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    </TableCell>
                    <TableCell className="text-sm text-green-600 max-w-[150px] truncate font-medium">
                      {change.after}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px]">
                      {change.reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
          {changes.length > 50 && (
            <p className="text-xs text-muted-foreground mt-2">Showing first 50 of {changes.length} changes.</p>
          )}
        </div>
      ) : (
        <Card className="p-6 text-center">
          <CheckCircle className="h-8 w-8 mx-auto text-green-500 mb-2" />
          <p className="text-sm text-muted-foreground">No field-level changes detected. Data was already clean, or AI only added enrichment fields.</p>
        </Card>
      )}
    </div>
  );
};

function inferReason(before: string, after: string): string {
  if (!before || before === '(empty)') return 'Missing value filled by AI';
  if (!after || after === '(empty)') return 'Invalid value removed';
  if (before.toLowerCase() === after.toLowerCase()) return 'Text formatting normalized';
  if (before.includes('@') && after.includes('@')) return 'Email format corrected';
  if (/^\+?\d/.test(before) && /^\+?\d/.test(after)) return 'Phone number standardized';
  if (before.length > after.length * 2) return 'Trimmed excessive content';
  return 'Value normalized by AI';
}
